import { useState, useCallback } from "react"
import { lumi } from "../lib/lumi"

export type IssueStatus = "issued" | "checking" | "resolved" | "unavailable"
export type ApplyStatus = "not_applied" | "applied"

export interface ReferencedUser {
  userId: string
  userName: string
  teamName?: string
  email?: string
}

export interface IssueAttachment {
  url: string
  name: string
  type?: string
}

export interface EquipmentIssue {
  _id: string
  equipmentId: string
  unitId?: string
  unitLabel?: string
  creator: string
  creatorName: string
  creatorEmail?: string
  referencedUsers?: ReferencedUser[]
  title?: string
  issueContent: string
  tags?: string[]
  attachments?: IssueAttachment[]
  resolutionContent?: string
  resolutionAttachments?: IssueAttachment[]
  resolvedAt?: string
  resolvedByName?: string
  expectedCause?: string
  possibleAction?: string
  status: IssueStatus
  applyStatus?: ApplyStatus
  actualCost?: number
  fieldComment?: string
  createdAt: string
  updatedAt: string
}

export interface IssueInput {
  unitId?: string
  unitLabel?: string
  title?: string
  issueContent: string
  tags?: string[]
  expectedCause?: string
  possibleAction?: string
  status: IssueStatus
  actualCost?: number
  fieldComment?: string
  referencedUsers?: ReferencedUser[]
  attachments?: IssueAttachment[]
}

export interface MentionedUser {
  userId: string
  userName: string
  email?: string
}

export interface ReadByEntry {
  userId: string
  userName: string
  readAt: string
}

export interface EquipmentComment {
  _id: string
  issueId: string
  equipmentId: string
  creator: string
  creatorName: string
  creatorEmail?: string
  content: string
  mentionedUsers?: MentionedUser[]
  attachments?: IssueAttachment[]
  readBy?: ReadByEntry[]
  createdAt: string
  updatedAt?: string
}

export function useEquipmentIssues() {
  const [issues, setIssues] = useState<EquipmentIssue[]>([])
  const [loading, setLoading] = useState(false)

  const fetchIssues = useCallback(async (equipmentId: string) => {
    if (!equipmentId) return
    setLoading(true)
    try {
      const { list } = await lumi.entities.equipment_issues.list({
        filter: { equipmentId },
        sort: { createdAt: -1 },
      })
      setIssues((list || []) as EquipmentIssue[])
      console.log("equipment_issues fetched:", list?.length)
    } catch (error) {
      console.error("이슈 목록 조회 실패:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  const createIssue = useCallback(
    async (
      equipmentId: string,
      data: IssueInput,
      meta: { creator: string; creatorName: string; creatorEmail?: string }
    ) => {
      const now = new Date().toISOString()
      const payload = {
        equipmentId,
        unitId: data.unitId || "",
        unitLabel: data.unitLabel || "공통항목",
        creator: meta.creator,
        creatorName: meta.creatorName,
        creatorEmail: meta.creatorEmail || "",
        referencedUsers: data.referencedUsers || [],
        title: data.title || "",
        issueContent: data.issueContent,
        tags: data.tags || [],
        attachments: data.attachments || [],
        expectedCause: data.expectedCause || "",
        possibleAction: data.possibleAction || "",
        status: data.status,
        applyStatus: "not_applied" as ApplyStatus,
        actualCost: typeof data.actualCost === "number" ? data.actualCost : 0,
        fieldComment: data.fieldComment || "",
        createdAt: now,
        updatedAt: now,
      }
      const created = await lumi.entities.equipment_issues.create(payload)
      console.log("equipment_issue created:", created?._id)
      await fetchIssues(equipmentId)
      return created
    },
    [fetchIssues]
  )

  const updateIssue = useCallback(
    async (equipmentId: string, id: string, data: Partial<IssueInput>) => {
      const payload: Record<string, any> = {
        updatedAt: new Date().toISOString(),
      }
      if (data.unitId !== undefined) payload.unitId = data.unitId
      if (data.unitLabel !== undefined) payload.unitLabel = data.unitLabel
      if (data.title !== undefined) payload.title = data.title
      if (data.issueContent !== undefined) payload.issueContent = data.issueContent
      if (data.tags !== undefined) payload.tags = data.tags
      if (data.expectedCause !== undefined) payload.expectedCause = data.expectedCause
      if (data.possibleAction !== undefined) payload.possibleAction = data.possibleAction
      if (data.status !== undefined) payload.status = data.status
      if (data.actualCost !== undefined) payload.actualCost = data.actualCost
      if (data.fieldComment !== undefined) payload.fieldComment = data.fieldComment
      if (data.referencedUsers !== undefined) payload.referencedUsers = data.referencedUsers
      if (data.attachments !== undefined) payload.attachments = data.attachments

      const updated = await lumi.entities.equipment_issues.update(id, payload)
      console.log("equipment_issue updated:", id)
      await fetchIssues(equipmentId)
      return updated
    },
    [fetchIssues]
  )

  const updateIssueStatus = useCallback(
    async (equipmentId: string, id: string, status: IssueStatus) => {
      const updated = await lumi.entities.equipment_issues.update(id, {
        status,
        updatedAt: new Date().toISOString(),
      })
      console.log("equipment_issue status updated:", id, status)
      await fetchIssues(equipmentId)
      return updated
    },
    [fetchIssues]
  )

  const updateApplyStatus = useCallback(
    async (equipmentId: string, id: string, applyStatus: ApplyStatus) => {
      const updated = await lumi.entities.equipment_issues.update(id, {
        applyStatus,
        updatedAt: new Date().toISOString(),
      })
      console.log("equipment_issue applyStatus updated:", id, applyStatus)
      await fetchIssues(equipmentId)
      return updated
    },
    [fetchIssues]
  )

  // 조치 결과(내용 + 사진) 저장
  const updateResolution = useCallback(
    async (
      equipmentId: string,
      id: string,
      data: {
        resolutionContent: string
        resolutionAttachments: IssueAttachment[]
        resolvedByName: string
      }
    ) => {
      const updated = await lumi.entities.equipment_issues.update(id, {
        resolutionContent: data.resolutionContent,
        resolutionAttachments: data.resolutionAttachments,
        resolvedByName: data.resolvedByName,
        resolvedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
      console.log("equipment_issue resolution updated:", id)
      await fetchIssues(equipmentId)
      return updated
    },
    [fetchIssues]
  )

  const deleteIssue = useCallback(
    async (equipmentId: string, id: string) => {
      await lumi.entities.equipment_issues.delete(id)
      console.log("equipment_issue deleted:", id)
      await fetchIssues(equipmentId)
    },
    [fetchIssues]
  )

  return {
    issues,
    loading,
    fetchIssues,
    createIssue,
    updateIssue,
    updateIssueStatus,
    updateApplyStatus,
    updateResolution,
    deleteIssue,
  }
}

export function useEquipmentComments() {
  const [comments, setComments] = useState<EquipmentComment[]>([])
  const [loading, setLoading] = useState(false)

  const fetchComments = useCallback(async (issueId: string) => {
    if (!issueId) return
    setLoading(true)
    try {
      const { list } = await lumi.entities.equipment_comments.list({
        filter: { issueId },
        sort: { createdAt: 1 },
      })
      setComments((list || []) as EquipmentComment[])
      console.log("equipment_comments fetched:", list?.length)
    } catch (error) {
      console.error("코멘트 목록 조회 실패:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  const createComment = useCallback(
    async (
      issueId: string,
      equipmentId: string,
      content: string,
      meta: { creator: string; creatorName: string; creatorEmail?: string },
      mentionedUsers: MentionedUser[] = [],
      attachments: IssueAttachment[] = []
    ) => {
      const now = new Date().toISOString()
      const payload = {
        issueId,
        equipmentId,
        creator: meta.creator,
        creatorName: meta.creatorName,
        creatorEmail: meta.creatorEmail || "",
        content,
        mentionedUsers,
        attachments,
        createdAt: now,
        updatedAt: now,
      }
      const created = await lumi.entities.equipment_comments.create(payload)
      console.log("equipment_comment created:", created?._id)
      await fetchComments(issueId)
      return created
    },
    [fetchComments]
  )

  const deleteComment = useCallback(
    async (issueId: string, id: string) => {
      await lumi.entities.equipment_comments.delete(id)
      console.log("equipment_comment deleted:", id)
      await fetchComments(issueId)
    },
    [fetchComments]
  )

  // 코멘트 읽음 처리 (한 번 읽으면 취소 불가)
  const markCommentRead = useCallback(
    async (
      issueId: string,
      comment: EquipmentComment,
      reader: { userId: string; userName: string }
    ) => {
      if (!reader.userId) return
      const already = (comment.readBy || []).some((r) => r.userId === reader.userId)
      if (already) return
      const nextReadBy: ReadByEntry[] = [
        ...(comment.readBy || []),
        { userId: reader.userId, userName: reader.userName, readAt: new Date().toISOString() },
      ]
      await lumi.entities.equipment_comments.update(comment._id, {
        readBy: nextReadBy,
        updatedAt: new Date().toISOString(),
      })
      console.log("equipment_comment read:", comment._id, reader.userId)
      await fetchComments(issueId)
    },
    [fetchComments]
  )

  return {
    comments,
    loading,
    fetchComments,
    createComment,
    deleteComment,
    markCommentRead,
  }
}
